import React, { useEffect, useState } from "react";
import Alert, { AlertColor } from "@mui/material/Alert";

import { encodeValue, toReadableText } from "../utils/utils";
import { buildLinkedInSearchUrl } from "../../utils/linkedinUtils";
import Modal from "@mui/material/Modal";
import Container from "@mui/material/Container";
import Zoom from "@mui/material/Zoom";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import LaunchIcon from "@mui/icons-material/Launch";
import DomainIcon from "@mui/icons-material/Domain";
import PublicIcon from "@mui/icons-material/Public";
import Typography from "@mui/material/Typography";
import { CirromLogoAvatar } from "../Logo";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import LinearProgress from "@mui/material/LinearProgress";
import {
  CirromMonitorCRMComparison,
  getMonitorCRMComparison,
} from "../../api/cirrommonitor";
import { EmailTrafficLight } from "../suggested-changes/RecordDetails";
import { MonitorComparisonTable } from "./MonitorComparisonTable";

export function SuggestedMonitorDetailsModal(
  props: Readonly<{
    open: boolean;
    onClose?: () => void;
    onSuccess: (id?) => void;
    onPreviousRowRequested?: () => void;
    onNextRowRequested?: () => void;
    recordId: number;
    integratedSystemId: number;
  }>
) {
  const { recordId, onPreviousRowRequested, onNextRowRequested } = props;
  const [suggestedExplain, setSuggestedExplain] =
    useState<CirromMonitorCRMComparison>(undefined);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const [contactUrl, setContactUrl] = useState<string>("");
  const [accountUrl, setAccountUrl] = useState<string>("");
  const [companyUrl, setCompanyUrl] = useState<string>(undefined);
  const [emailStatuses, setEmailStatuses] = useState<string[][]>([]);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);

  /**
   * Generate email status messages based on the provided data
   */
  const generateEmailStatusMessage = (data) => {
    const statuses = [];
    if (data.last_email_activity) {
      statuses.push([
        "success",
        `Last email activity on ${new Date(
          Date.parse(data.last_email_activity)
        ).toDateString()}.`,
      ]);
    }

    if (data.last_email_hard_bounce) {
      statuses.push([
        "error",
        `Email hard bounce event on ${new Date(
          Date.parse(data.last_email_hard_bounce)
        ).toDateString()}.`,
      ]);
    }

    if (data.last_email_soft_bounce) {
      statuses.push([
        "warning",
        `Email soft bounce event on ${new Date(
          Date.parse(data.last_email_soft_bounce)
        ).toDateString()}.`,
      ]);
    }

    if (statuses.length === 0) {
      statuses.push(["warning", "This contact has never been emailed."]);
    }

    setEmailStatuses(statuses);
  };

  /**
   * fetchSuggestedChangeExplain function
   *
   * Retrieve the data required to populate the modal
   */
  const fetchSuggestedChangeExplain = () => {
    getMonitorCRMComparison(recordId, props.integratedSystemId)
      .then((resp) => {
        setSuggestedExplain(resp);
        if (resp.crm_results) {
          generateEmailStatusMessage(resp.crm_results);
        }
      })
      .catch(() => {
        setAlertSeverity("error");
        setAlertText("Failed to load contact details. Please try again.");
        setAlertOpen(true);
      });
  };

  useEffect(() => {
    setSuggestedExplain(undefined);
    setAlertOpen(false);
    setContactUrl("");
    setAccountUrl("");
    setCompanyUrl("");
    setRequestInProgress(false);
    if (recordId) {
      fetchSuggestedChangeExplain();
    }
  }, [recordId]);

  /**
   * getContactSearchURL
   * get google/linkedin search urls
   * @param isGoogle
   */
  const getContactSearchURL = (isGoogle?: boolean) => {
    const firstName = suggestedExplain.crm_results.first_name;
    const lastName = suggestedExplain.crm_results.last_name;
    const company = suggestedExplain.crm_results.company;

    if (isGoogle) {
      const encodedFirstName = encodeValue(firstName);
      const encodedLastName = encodeValue(lastName);
      const encodedCompany = encodeValue(company);
      return `https://www.google.com/search?q=${encodedFirstName} ${encodedLastName} ${encodedCompany}`;
    }
    return buildLinkedInSearchUrl({ firstName, lastName, company });
  };

  const openExternalLookup = (isGoogle) => {
    window.open(getContactSearchURL(isGoogle), "_blank");
  };

  return (
    <Modal
      open={props.open}
      onClose={() => {
        props.onClose?.();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container
        maxWidth={suggestedExplain ? "md" : "sm"}
        style={{
          padding: "0px",
        }}
      >
        {suggestedExplain ? (
          <Zoom in={true}>
            <Card>
              <CardHeader
                style={{ padding: "20px", marginBottom: "14px" }}
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2rem",
                    }}
                  >
                    {`${suggestedExplain.crm_results.first_name} ${suggestedExplain.crm_results.last_name}`}
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      {contactUrl && (
                        <Tooltip title={"View Contact in CRM"}>
                          <LaunchIcon
                            style={{ width: "20px", cursor: "pointer" }}
                            onClick={() => window.open(contactUrl, "_blank")}
                          />
                        </Tooltip>
                      )}

                      {accountUrl && (
                        <>
                          <Divider
                            orientation="vertical"
                            variant="middle"
                            flexItem
                          />
                          <Tooltip title={"View Account in CRM"}>
                            <DomainIcon
                              style={{ width: "20px", cursor: "pointer" }}
                              onClick={() => window.open(accountUrl, "_blank")}
                            />
                          </Tooltip>
                        </>
                      )}

                      {companyUrl && (
                        <>
                          <Divider
                            orientation="vertical"
                            variant="middle"
                            flexItem
                          />
                          <Tooltip title={"Open Company Website"}>
                            <PublicIcon
                              style={{ width: "20px", cursor: "pointer" }}
                              onClick={() => window.open(companyUrl, "_blank")}
                            />
                          </Tooltip>
                        </>
                      )}

                      <>
                        <Divider
                          orientation="vertical"
                          variant="middle"
                          flexItem
                        />
                        <Tooltip title={"Open in LinkedIn"}>
                          <img
                            src={"/linkedin-logo.svg"}
                            style={{ width: "18px", cursor: "pointer" }}
                            onClick={() => {
                              if (
                                suggestedExplain?.pending_results &&
                                suggestedExplain.pending_results[0].linkedin_url
                              ) {
                                window.open(
                                  suggestedExplain.pending_results[0]
                                    .linkedin_url,
                                  "_blank"
                                );
                              } else {
                                openExternalLookup(false);
                              }
                            }}
                            onKeyDown={() =>
                              window.open(getContactSearchURL(), "_blank")
                            }
                            alt={"linkedin-logo"}
                            role={"presentation"}
                          />
                        </Tooltip>
                      </>

                      <>
                        <Divider
                          orientation="vertical"
                          variant="middle"
                          flexItem
                        />
                        <Tooltip title={"Search on Google"}>
                          <img
                            src={"/google-logo.png"}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                            }}
                            onKeyDown={() =>
                              window.open(getContactSearchURL(true), "_blank")
                            }
                            onClick={() => {
                              openExternalLookup(true);
                            }}
                            alt={"google-logo"}
                            role={"presentation"}
                          />
                        </Tooltip>
                      </>
                    </div>
                  </div>
                }
                titleTypographyProps={{ variant: "h3" }}
                subheader={
                  <div>
                    <>
                      <div>
                        {suggestedExplain.crm_results.contact_source &&
                          ` Sourced from: ${toReadableText(
                            suggestedExplain.crm_results.contact_source
                          )}`}
                        {suggestedExplain.crm_results.contact_source &&
                          suggestedExplain.crm_results.job_title &&
                          " | "}
                        {`${suggestedExplain.crm_results.job_title || ""}`}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          marginTop: "5px",
                        }}
                      >
                        {emailStatuses.map((status, index) => (
                          <EmailTrafficLight
                            variant={status[0]}
                            status={status[1]}
                            key={`${status[0]}-${index}`}
                          />
                        ))}
                      </div>
                    </>
                  </div>
                }
                avatar={
                  <CirromLogoAvatar
                    styleOverrides={{
                      width: "64px",
                      height: "64px",
                    }}
                  />
                }
                action={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        props.onClose?.();
                      }}
                      color={"error"}
                    >
                      <CloseOutlinedIcon />
                    </IconButton>
                    {(onPreviousRowRequested || onNextRowRequested) && (
                      <div style={{ display: "flex", gap: "4px" }}>
                        <IconButton
                          onClick={() => onPreviousRowRequested?.()}
                          color={"info"}
                          disabled={!onPreviousRowRequested}
                          aria-label="Previous contact"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "0px",
                          }}
                        >
                          <NavigateBefore />
                          <Typography variant={"caption"}>Prev</Typography>
                        </IconButton>
                        <IconButton
                          onClick={() => onNextRowRequested?.()}
                          color={"info"}
                          disabled={!onNextRowRequested}
                          aria-label="Next contact"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "0px",
                          }}
                        >
                          <NavigateNext />
                          <Typography variant={"caption"}>Next</Typography>
                        </IconButton>
                      </div>
                    )}
                  </div>
                }
              />
              <Collapse in={alertOpen}>
                <Alert
                  severity={alertSeverity}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setAlertOpen(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {alertText}
                </Alert>
              </Collapse>
              {requestInProgress && <LinearProgress />}

              <Divider />
              <Container
                style={{
                  minHeight: "400px",
                  maxHeight: "calc(100vh - 200px)",
                  overflowY: "scroll",
                }}
              >
                <MonitorComparisonTable
                  suggestedExplain={suggestedExplain}
                  recordId={recordId}
                  integratedSystemId={props.integratedSystemId}
                  requestInProgress={requestInProgress}
                  setRequestInProgress={setRequestInProgress}
                  onSuccess={() => {
                    fetchSuggestedChangeExplain();
                    props.onSuccess();
                  }}
                  displayAlert={(text, severity) => {
                    setAlertSeverity(severity as any);
                    setAlertText(text);
                    setAlertOpen(true);
                  }}
                />
              </Container>

              <Container
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              ></Container>
            </Card>
          </Zoom>
        ) : null}
      </Container>
    </Modal>
  );
}
